'use client';

import { useState, useEffect } from 'react';
import { Avatar } from './Avatar';
import { UserWithMentor } from '@/types/mentor';

interface FloatingAvatarsBackgroundProps {
  mentors: UserWithMentor[];
}

export function FloatingAvatarsBackground({ mentors }: FloatingAvatarsBackgroundProps) {
  const [showAvatars, setShowAvatars] = useState(false);

  useEffect(() => {
    // Delay rendering to not block initial paint
    const timeout = setTimeout(() => setShowAvatars(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  if (!showAvatars || mentors.length === 0) return null;

  // Take up to 8 mentors for the background
  const displayMentors = mentors.slice(0, 8);

  return (
    <div style={{ 
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 0,
    }}>
      {displayMentors.map((mentor, index) => {
        const position = getPosition(index);
        const size = getSize(index);
        const animationDelay = index * 2; // Stagger animations
        const animationDuration = 15 + (index * 3); // Vary speeds
        
        return (
          <div
            key={mentor.id}
            style={{
              position: 'absolute',
              left: `${position.x}%`,
              top: `${position.y}%`,
              opacity: 0.2,
              animation: `float-upward-${index % 8} ${animationDuration}s ease-in-out ${animationDelay}s infinite`,
              filter: `blur(${4 + (index % 3) * 4}px)`,
            }}
          >
            <Avatar
              userId={mentor.id}
              avatarId={mentor.avatarId}
              size={size}
              radius="xl"
              loading="lazy"
              priority={false}
            />
          </div>
        );
      })}
    </div>
  );
}

function getPosition(index: number) {
  const positions = [
    { x: 5, y: 10 },
    { x: 85, y: 15 },
    { x: 10, y: 60 },
    { x: 90, y: 65 },
    { x: 25, y: 30 },
    { x: 70, y: 35 },
    { x: 40, y: 80 },
    { x: 60, y: 5 },
  ];
  return positions[index] || { x: 50, y: 50 };
}

function getSize(index: number) {
  const sizes = [100, 80, 120, 90, 110, 85, 95, 105];
  return sizes[index] || 100;
}
